import { useEffect, useState } from 'react';
import cx from 'classnames';
import { useAnalytics } from 'edrolo/analytics';
import { Popover, usePopover, ClickAwayListener, enums } from 'edrolo-ui';
import { useSelfRating } from './useSelfRating';
import { selfRatingOptions } from './constants';
import { SelfRatingPopover } from './SelfRatingPopover';

import styles from './styles.scss';

interface SelfRatingDialogProps {
  userId: number;
  classId: number;
  lessonId: number;
}

export const SelfRatingDialog = ({
  userId,
  classId,
  lessonId,
}: SelfRatingDialogProps): JSX.Element | null => {
  const [selfRating, setSelfRating] = useState<number | null>(null);
  const { isOpen, anchor, togglePopover, closePopover } = usePopover();
  const {
    data: selfRatingValue,
    error,
    loading,
    updateSelfRating,
  } = useSelfRating(userId, classId, lessonId);

  const { track } = useAnalytics({});

  useEffect(() => {
    if (selfRatingValue) {
      setSelfRating(selfRatingValue);
    }
    // The clean up function is required to prevent a memory leak
    return () => {
      setSelfRating(null);
    };
  }, [selfRatingValue]);

  const selfRatingButton = selfRatingOptions.find(
    (option) => option.rating === selfRating
  );

  if (error || loading) {
    return null;
  }

  const handleSelfRating = (rating: number) => {
    updateSelfRating(rating, lessonId);
    setSelfRating(rating);
    track('lesson rating updated');
    closePopover();
  };

  return (
    <ClickAwayListener onClickAway={closePopover}>
      <div>
        <button
          className={cx(styles.button, {
            [styles[`${selfRatingButton?.color}`]]: selfRatingButton,
          })}
          onClick={togglePopover}
        >
          {selfRating}
        </button>
        <Popover
          isOpen={isOpen}
          anchor={anchor}
          placement={enums.Placement.BOTTOM_END}
          closePopover={closePopover}
        >
          <SelfRatingPopover onSetSelfRating={handleSelfRating} />
        </Popover>
      </div>
    </ClickAwayListener>
  );
};
